import { useRef } from 'react'
import type { ReactNode } from 'react'
import s from './Tabs.module.scss'

export interface TabItem {
  /** Steht so in der Adresse. Kurz und ohne Umlaute. */
  key: string
  label: string
  /** Rechts neben der Beschriftung, gedämpft — etwa eine Anzahl. */
  badge?: ReactNode
  disabled?: boolean
}

export interface TabsProps {
  items: TabItem[]
  active: string
  onChange: (key: string) => void
  /** Für die Zuordnung zu den Inhalten: `aria-controls` je Reiter. */
  panelId?: string
  'aria-label'?: string
}

/**
 * Umschalter zwischen Sichten auf denselben Inhalt.
 *
 * **Kontrolliert**, wie `MultiSelect`: Der Zustand liegt beim Aufrufer. Nur so
 * kann die Seite ihn in die Adresse schreiben — eine Sicht, die sich nicht
 * verlinken lässt, ist die halbe Sicht.
 *
 * Der aktive Reiter trägt einen Balken DARUNTER, keinen Rahmen ringsum. Das ist
 * ruhiger und passt zum `label`-Strich, den `Card` schon benutzt; ein Rahmen
 * bildet eine zweite Kante neben der Trennlinie und lässt den Reiter wie einen
 * Knopf aussehen, der er nicht ist.
 *
 * Tastatur: Pfeil links und rechts wechseln, Pos1 und Ende springen an den Rand
 * — und zwar über einen **Roving Tabindex**. Nur der aktive Reiter ist mit Tab
 * erreichbar, danach führt Tab in den Inhalt. Alle Reiter einzeln antabben zu
 * müssen wäre bei drei Sichten schon lästig; die ARIA-Empfehlung für `tablist`
 * ist genau deshalb diese.
 */
export function Tabs({ items, active, onChange, panelId,
                       'aria-label': ariaLabel }: TabsProps) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({})

  // Gesprungen wird nur zwischen den WÄHLBAREN: Ein deaktivierter Reiter, auf
  // dem der Fokus landet, ist eine Sackgasse — er nimmt ihn an und tut nichts.
  const waehlbar = items.filter((item) => !item.disabled)

  function wechseln(richtung: number | 'erster' | 'letzter') {
    if (waehlbar.length === 0) return
    const jetzt = waehlbar.findIndex((item) => item.key === active)
    const ziel = richtung === 'erster' ? 0
      : richtung === 'letzter' ? waehlbar.length - 1
      // Modulo mit Vorzeichenausgleich: In JavaScript ist -1 % 3 gleich -1
      // und nicht 2. Ohne den Ausgleich läuft Pfeil-links auf dem ersten
      // Reiter ins Leere statt auf den letzten.
      : (jetzt + richtung + waehlbar.length) % waehlbar.length

    const neu = waehlbar[ziel]
    if (!neu) return
    onChange(neu.key)
    // Der Fokus muss mitgehen, sonst hängt er auf dem alten Reiter und die
    // nächste Pfeiltaste käme dort wieder an.
    refs.current[neu.key]?.focus()
  }

  return (
    <div className={s.leiste} role="tablist" aria-label={ariaLabel}>
      {items.map((item) => {
        const an = item.key === active
        return (
          <button key={item.key} type="button" role="tab"
                  ref={(element) => { refs.current[item.key] = element }}
                  className={`${s.reiter} ${an ? s.an : ''}`}
                  aria-selected={an}
                  aria-controls={panelId}
                  disabled={item.disabled}
                  // Roving Tabindex: nur der aktive ist in der Tab-Reihenfolge.
                  tabIndex={an ? 0 : -1}
                  onClick={() => onChange(item.key)}
                  onKeyDown={(event) => {
                    const taste = {
                      ArrowRight: 1, ArrowLeft: -1,
                      Home: 'erster', End: 'letzter',
                    }[event.key] as number | 'erster' | 'letzter' | undefined
                    if (taste === undefined) return
                    event.preventDefault()
                    wechseln(taste)
                  }}>
            <span className={s.text}>{item.label}</span>
            {item.badge !== undefined && item.badge !== null &&
              <span className={s.badge}>{item.badge}</span>}
          </button>
        )
      })}
    </div>
  )
}
